import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {filter, switchMap, tap} from 'rxjs/operators';
import { merge } from 'es-toolkit';
import {environment} from '../../environments/environment';
import {ContactsService} from '../contacts/contacts.service';
import {BaseService} from '@shared/services/base.service';
import {ToastService} from '@shared/services/toast.service';


@Injectable({
  providedIn: 'root'
})
export class UstService extends BaseService {
  public item: any = {};
  public meta: any = {};
  public tanksService: BaseService;
  public inspectionsService: BaseService;
  public permitsService: BaseService;
  public actionsService: BaseService;
  public preventionsService: BaseService;
  public releasesService: BaseService;
  public overfillProjectionService: BaseService;
  public spillProjectionService: BaseService;
  public permitContactsService: BaseService;
  public atgManufacturerService: BaseService;
  public atgModelService: BaseService;
  public fieldCitationsService: BaseService;
  public sirVendorService: BaseService;
  public sirVersionService: BaseService;
  public otherTankService: BaseService;
  public otherLineService: BaseService;
  private currentSearchObj: any;

  constructor(http: HttpClient, private contactsService: ContactsService, private toastService: ToastService) {
    super(`${environment.baseUrl}/ust/sites`, http);
    this.tanksService = new BaseService(`${environment.baseUrl}/ust/tanks`, http);
    this.inspectionsService = new BaseService(`${environment.baseUrl}/ust/inspections`, http);
    this.permitsService = new BaseService(`${environment.baseUrl}/ust/permits`, http);
    this.actionsService = new BaseService(`${environment.baseUrl}/ust/actions`, http);
    this.preventionsService = new BaseService(`${environment.baseUrl}/ust/preventions`, http);
    this.releasesService = new BaseService(`${environment.baseUrl}/ust/rd_records`, http);
    this.overfillProjectionService = new BaseService(`${environment.baseUrl}/ust/tanks/overfill`, http);
    this.spillProjectionService = new BaseService(`${environment.baseUrl}/ust/tanks/spillprotection`, http);
    this.permitContactsService = new BaseService(`${environment.baseUrl}/ust/permits/contacts`, http);
    this.atgManufacturerService = new BaseService(`${environment.baseUrl}/ust/preventions/atgmanufacturer`, http);
    this.atgModelService = new BaseService(`${environment.baseUrl}/ust/preventions/atgmodel`, http);
    this.fieldCitationsService = new BaseService(`${environment.baseUrl}/ust/fieldcitations`, http);
    this.sirVendorService = new BaseService(`${environment.baseUrl}/ust/preventions/sirvendor`, http);
    this.sirVersionService = new BaseService(`${environment.baseUrl}/ust/preventions/sirversion`, http);
    this.otherTankService = new BaseService(`${environment.baseUrl}/ust/preventions/othertank`, http);
    this.otherLineService = new BaseService(`${environment.baseUrl}/ust/preventions/otherline`, http);
  }

  public loadData(search: any): Observable<any> {
    this.currentSearchObj = search;
    return this.getList<any>(search).pipe(
      tap(r => {
        if (r.results[0]) {
          merge(this.item, r.results[0]);
        } else {
          this.item = {};
        }
        merge(this.meta, r.meta);
        // some meta has to be loaded even if no item is available
        this.meta.display_fields = ['ust_id', 'ust_active_count', 'ust_compliance', 'ust_actions', 'ust_past_due', 'ust_last_inspection', 'ust_lust'];
        this.meta.type = 'ust/sites';
        this.meta.urlPath = 'ust';
      }),
      filter(response => response.results.length > 0),
      switchMap(r => forkJoin({
          site: of(r),
          tanks_meta: this.tanksService.get<any>('metadata'),
          inspections_meta: this.inspectionsService.get<any>('metadata'),
          permits_meta: this.permitsService.get<any>('metadata'),
          actions_meta: this.actionsService.get<any>('metadata'),
          preventions_meta: this.preventionsService.get<any>('metadata'),
          releases_meta: this.releasesService.get<any>('metadata'),
        })
      ),
      tap(responses => {

        this.meta.subItems = ['permits', 'tanks', 'preventions', 'inspections', 'actions', 'attachments'];
        this.meta.fields.attachments = {type: 'ust/sitesattach'};
        this.meta.fields.owner.get_choices = (searchObj) => {
          return new Promise(resolve => {
            this.contactsService.get_choices(searchObj).then(results => {
              const choices = results.map(contact => ({
                display_name: contact.display_name,
                value: contact.globalid
              }));
              resolve(choices);
            });
          });
        };
        this.meta.fields.site_contact.get_choices = (searchObj) => {
          return new Promise(resolve => {
            this.contactsService.get_choices(searchObj).then(results => {
              const choices = results.map(contact => ({
                display_name: contact.display_name,
                value: contact.globalid
              }));
              resolve(choices);
            });
          });
        };
        this.meta.fields.tanks = responses.tanks_meta;
        this.meta.fields.tanks.label_field = 'number';
        this.meta.fields.tanks.label_color = [115, 178, 255, 1];
        this.meta.fields.tanks.featureService = 'https://gis.epa.guam.gov/arcgis/rest/services/StorageTanks/FeatureServer/1';
        this.meta.fields.tanks.type = 'ust/tanks';
        this.meta.fields.tanks.foreign_key_field = 'facility';
        this.meta.fields.tanks.add_another_reset = ['tank_id', 'tank_ul', 'number'];
        this.meta.fields.tanks.display_fields = [
          'tank_id', 'tank_ul', 'number', 'permits', 'install_date', 'type', 'status', 'capacity',
          'contents', 'fed_regulated', 'tank_material', 'tank_description', 'emergency_power',
          'pipe_material', 'pipe_description', 'pipe_type', 'pipe_repair', 'overfill_protection',
          'spill_protection', 'horizontal_collection_method'
        ];
        this.meta.fields.tanks.fields.permits.get_choices = () => this.getPermitChoices();
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.tanks.fields.overfill_protection.get_choices = search => {
          search = search ? search : '';
          return new Promise(resolve => {
            this.overfillProjectionService.getList<any>({search}).pipe(tap(r => {
              // @ts-ignore
              const choices = r.map(item => ({display_name: item, value: item}));
              resolve(choices);
            })).subscribe();
          });
        };
        this.meta.fields.tanks.fields.overfill_protection.select_choice = (selected, item) => {
          item.overfill_protection = selected.value;
        };
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.tanks.fields.spill_protection.get_choices = search => {
          search = search ? search : '';
          return new Promise(resolve => {
            this.spillProjectionService.getList<any>({search}).pipe(tap(r => {
              // @ts-ignore
              const choices = r.map(item => ({display_name: item, value: item}));
              resolve(choices);
            })).subscribe();
          });
        };
        this.meta.fields.tanks.fields.spill_protection.select_choice = (selected, item) => {
          item.spill_protection = selected.value;
        };
        this.meta.fields.tanks.subItems = ['comments', 'closures', 'lustevent'];
        this.meta.fields.tanks.comments = {
          type: 'ust/comments',
          display_fields: [
            'text', 'created_date', 'created_user', 'last_edited_date', 'last_edited_user'
          ],
          foreign_key_field: 'tank'
        };

        this.meta.fields.tanks.closures = {
          type: 'ust/closures',
          display_fields: [
            'closure_id', 'closure_date', 'corrosion_protection_maint',
            'release_detection_maint', 'ust_empty', 'vents_open', 'lines_capped', 'ust_upgraded',
            'closure_notice', 'change_of_service', 'site_assessment', 'action_required', 'comments'
          ],
          foreign_key_field: 'tank',
          fields: {closure_id: {}}
        };
        this.meta.fields.tanks.closures.fields.closure_id.get_choices = () => this.getPermitStrChoices();
        this.meta.fields.tanks.lustevent = {
          type: 'ust/lustevent',
          display_fields: [
            'event_id', 'event_date', 'status', 'how_discovered', 'event_priority', 'lust_source', 'lust_cause', 'lust_funding', 'cleanup_money', 'emergency_response', 'lust_milestone', 'event_closed_date'
          ],
          foreign_key_field: 'tank',
          refreshProperties: () => this.refreshTopLevelProperties()
        };
        this.meta.fields.permits = responses.permits_meta;
        this.meta.fields.permits.type = 'ust/permits';
        this.meta.fields.permits.foreign_key_field = 'facility';
        this.meta.fields.permits.display_fields = [
          'permit_id', 'tanks', 'type', 'issued_date', 'application_date', 'old_permit_number',
        ];
        this.meta.fields.permits.subItems = ['contacts', 'review'];
        this.meta.fields.permits.contacts = {
          type: 'ust/permits/contacts',
          display_fields: ['relationship_type', 'contact_display_name', 'contact_contact_type'],
          foreign_key_field: 'ust_permits',
          fields: {
            contact_first_name: {
              get_choices: (s) => this.contactsService.meta.fields.contact_first_name.get_choices(s),
              select_choice: (selected, item) => this.select_choice(selected, item),
              choices: null
            },
            contact_org_name: {
              get_choices: (s) => this.contactsService.meta.fields.contact_org_name.get_choices(s),
              select_choice: (selected, item) => this.select_choice(selected, item),
              choices: null
            }
          }
        };

        this.meta.fields.permits.review = {
          verbose_name_plural: 'Routing/Review',
          verbose_name: 'Review',
          display_fields: ['reviewer', 'status', 'reason', 'order', 'last_edited_date']
        };

        this.meta.fields.permits.fields.tanks.get_choices = () => this.getTankChoices();

        this.meta.fields.inspections = responses.inspections_meta;
        this.meta.fields.inspections.label_field = 'inspection_id';
        this.meta.fields.inspections.label_color = [115, 223, 255, 1];
        this.meta.fields.inspections.featureService = 'https://gis.epa.guam.gov/arcgis/rest/services/StorageTanks/FeatureServer/0';
        this.meta.fields.inspections.type = 'ust/inspections';
        this.meta.fields.inspections.foreign_key_field = 'facility';
        this.meta.fields.inspections.display_fields = [
          'inspection_id', 'inspection_date', 'notification_form', 'tanks', 'records_maintained', 'comments',
          'maintenance_test_date', 'repair_records', 'repair_desc', 'repairs_tested', 'atg_model', 'rd_method_tank',
          'rd_method_line', 'release_detection_records', 'compatible_substance', 'lld_type', 'mlld_date', 'plld_date',
          'q1test_date', 'q2test_date', 'q3test_date', 'rd_performance_claims', 'release_detection_details',
          'insurance_records', 'insurance_details', 'inspector', 'status', 'horizontal_collection_method'
        ];
        this.meta.fields.inspections.fields.tanks.get_choices = () => this.getTankChoices();
        this.meta.fields.inspections.subItems = ['rd_records', 'violations', 'comments', 'signature_details', 'attachments'];
        this.meta.fields.inspections.violations = {
          type: 'ust/violations',
          display_fields: ['citation', 'violation', 'settlement', 'remarks', 'soc'],
          foreign_key_field: 'inspection',
          fields: {citation: {}}
        };
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.inspections.violations.fields.citation.get_choices = search => {
          return new Promise(resolve => {
            search = search ? search : '';
            this.fieldCitationsService.getList<any>({
              page_size: 1000,
              search
            }).subscribe(response => {
              const choices = response.results.map(item => {
                item.display_name = item.violation + ' (§' + item.local_code + ')';
                item.value = item.local_code;
                return item;
              });
              resolve(choices);
            });
          });
        };
        this.meta.fields.inspections.violations.fields.citation.select_choice = (selected, item) => {
          item.violation = selected.violation;
          item.settlement = selected.fee;
          item.soc = selected.soc;
        };
        this.meta.fields.inspections.rd_records = {
          type: 'ust/rd_records',
          display_fields: [
            'record_type', 'tank_contents', 'record_desc', 'available_records', 'release_detection_results', 'tightness_testing_results', 'spill_prevention', 'overfill_prevention'
          ],
          foreign_key_field: 'inspection'
        };

        this.meta.fields.inspections.comments = {
          type: 'ust/comments',
          display_fields: [
            'text', 'created_date', 'created_user', 'last_edited_date', 'last_edited_user'
          ],
          foreign_key_field: 'inspection'
        };
        this.meta.fields.inspections.signature_details = {
          type: 'ust/inspectionssignaturedetails',
          display_fields: [
            // 'responsible_party', 'agency', 'email_address', 'send_report_yn', 'report_sent_date'
            'responsible_party', 'agency', 'email_address'
          ],
          foreign_key_field: 'rel_globalid'
        };
        this.meta.fields.inspections.signature_details.subItems = ['signature_attachments'];
        this.meta.fields.inspections.signature_details.signature_attachments = {
          type: 'ust/inspectionssignature',
          use_verbose_name: true
        };
        this.meta.fields.inspections.attachments = {type: 'ust/inspectionsattach'};

        this.meta.fields.inspections.print = {
          label: 'Print',
          tooltip: 'Generate Notice of Inspection',
          get: item => {
            return new Promise<void>(resolve => {
              this.inspectionsService.get<any>(`${item.globalid}/get_inspection_report`).subscribe(response => {
                if (response.hasOwnProperty('error')) {
                  this.toastService.warning(response.error);
                } else {
                  const noiID = item.inspection_id;
                  const noiURL = `${environment.baseUrl}api/ust/inspection_report?id=${noiID}/`;
                  window.open(noiURL, '_blank');
                }
                resolve();
              });
            });
          }
        };

        this.meta.fields.actions = responses.actions_meta;
        this.meta.fields.actions.type = 'ust/actions';
        this.meta.fields.actions.foreign_key_field = 'facility';
        this.meta.fields.actions.display_fields = [
          'inspection', 'type', 'issued_date', 'due_date', 'thirty_day_extension',
          'received_by_name', 'received_by_signature', 'response', 'approval', 'approval_date'
        ];
        this.meta.fields.actions.fields.inspection.get_choices = () => {
          return new Promise(resolve => {
            this.inspectionsService.getList<any>({search: this.item.globalid}).subscribe(response => {
              const choices = response.results.map(inspection => ({
                display_name: inspection.inspection_id,
                value: inspection.globalid
              }));
              resolve(choices);
            });
          });
        };
        this.meta.fields.actions.subItems = ['violations', 'comments', 'attachments'];
        this.meta.fields.actions.violations = {
          type: 'ust/violations',
          display_fields: ['citation', 'violation', 'soc', 'remarks'],
          foreign_key_field: 'enforcement',
          fields: {citation: {}},
          hide_add_button: true,
          hide_delete_button: true
        };

        this.meta.fields.actions.comments = {
          type: 'ust/comments',
          display_fields: ['text', 'created_date', 'created_user', 'last_edited_date', 'last_edited_user'],
          foreign_key_field: 'enforcement'
        };
        this.meta.fields.actions.attachments = {type: 'ust/actionsattach'};

        this.meta.fields.preventions = responses.preventions_meta;
        this.meta.fields.preventions.type = 'ust/preventions';
        this.meta.fields.preventions.foreign_key_field = 'facility';
        this.meta.fields.preventions.display_fields = [
          'tanks', 'tank_gauging', 'tank_tightness', 'inventory_control', 'automatic_gauging', 'atg_mfg',
          'atg_model', 'vapor_monitoring', 'ground_water_monitoring', 'sir', 'sir_vendor', 'sir_version',
          'interstitial_dbl_wall_monitor', 'interstitial_sec_cont_monitor', 'line_leak_detector', 'line_tightness_testing',
          'other_method_tank', 'other_method_line', 'deferred', 'not_listed', 'frp', 'cathodic_protection',
          'impressed_current', 'sacrificial_anodes', 'interior_lining'
        ];
        this.meta.fields.preventions.fields.tanks.get_choices = () => this.getTankChoices();
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.preventions.fields.atg_mfg.get_choices = search => {
          return new Promise(resolve => {
            search = search ? search : '';
            this.atgManufacturerService.getList<any>({search}).subscribe(response => {
              // @ts-ignore
              const choices = response.map(item => ({display_name: item, value: item}));
              resolve(choices);
            });
          });
        };
        this.meta.fields.preventions.fields.atg_mfg.select_choice = (selected, item) => {
          item.atg_mfg = selected.value;
        };
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.preventions.fields.atg_model.get_choices = search => {
          return new Promise(resolve => {
            search = search ? search : '';
            this.atgModelService.getList<any>({search}).subscribe(response => {
              // @ts-ignore
              const choices = response.map(item => ({display_name: item, value: item}));
              resolve(choices);
            });
          });
        };
        this.meta.fields.preventions.fields.atg_model.select_choice = (selected, item) => {
          item.atg_model = selected.value;
        };
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.preventions.fields.sir_vendor.get_choices = search => {
          return new Promise(resolve => {
            search = search ? search : '';
            this.sirVendorService.getList<any>({search}).subscribe(response => {
              // @ts-ignore
              const choices = response.map(item => ({display_name: item, value: item}));
              resolve(choices);
            });
          });
        };
        this.meta.fields.preventions.fields.sir_vendor.select_choice = (selected, item) => {
          item.sir_vendor = selected.value;
        };
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.preventions.fields.sir_version.get_choices = search => {
          return new Promise(resolve => {
            search = search ? search : '';
            this.sirVersionService.getList<any>({search}).subscribe(response => {
              // @ts-ignore
              const choices = response.map(item => ({display_name: item, value: item}));
              resolve(choices);
            });
          });
        };
        this.meta.fields.preventions.fields.sir_version.select_choice = (selected, item) => {
          item.sir_version = selected.value;
        };
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.preventions.fields.other_method_tank.get_choices = search => {
          return new Promise(resolve => {
            search = search ? search : '';
            this.otherTankService.getList<any>({search}).subscribe(response => {
              // @ts-ignore
              const choices = response.map(item => ({display_name: item, value: item}));
              resolve(choices);
            });
          });
        };
        this.meta.fields.preventions.fields.other_method_tank.select_choice = (selected, item) => {
          item.other_method_tank = selected.value;
        };
        // tslint:disable-next-line:no-shadowed-variable
        this.meta.fields.preventions.fields.other_method_line.get_choices = search => {
          return new Promise(resolve => {
            search = search ? search : '';
            this.otherLineService.getList<any>({search}).subscribe(response => {
              // @ts-ignore
              const choices = response.map(item => ({display_name: item, value: item}));
              resolve(choices);
            });
          });
        };
        this.meta.fields.preventions.fields.other_method_line.select_choice = (selected, item) => {
          item.other_method_line = selected.value;
        };

        this.meta.subItems.forEach(item => {
          this.meta.fields[item].refreshProperties = () => this.refreshTopLevelProperties();
        });
      }));
  }


  private getTankChoices() {
    return new Promise(resolve => {
      this.tanksService.getList<any>({search: this.item.globalid}).subscribe((response) => {
        const choices = response.results.map(item => ({display_name: item.number, value: item.globalid}));
        resolve(choices);
      });
    });
  }

  private getPermitChoices() {
    return new Promise(resolve => {
      this.permitsService.getList<any>({search: this.item.globalid}).subscribe(response => {
        const choices = response.results.map(item => ({display_name: item.permit_id, value: item.globalid}));
        resolve(choices);
      });
    });
  }

  private getPermitStrChoices() {
    return new Promise(resolve => {
      this.permitsService.getList<any>({search: this.item.globalid}).subscribe(response => {
        const choices = response.results.map(item => ({display_name: item.permit_id, value: item.permit_id}));
        resolve(choices);
      });
    });
  }

  private refreshTopLevelProperties() {
    this.getList(this.currentSearchObj).subscribe(response => {
      merge(this.item, response.results[0]);
    });
  }

  private select_choice(selected, item) {
    Object.keys(selected).forEach(key => item['contact_' + key] = selected[key])
  }

  private get_choices(searchObj) {
    return new Promise(resolve => {
      this.permitContactsService.getList({search: searchObj}).pipe(tap(results => {
        resolve(results);
      })).subscribe();
    });
  }
}
